import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, LogOut } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const TABS = ["Leads", "Portfolio", "Testimonials"];

const emptyPortfolio = { title: "", category: "Concerts", city: "", venue: "", date: "", description: "", image: "", services: "", kind: "project", published: true };
const emptyTestimonial = { name: "", role: "", quote: "", event: "", published: true };

export default function Admin() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("Leads");
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState(null);
  const [portfolio, setPortfolio] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [pForm, setPForm] = useState(emptyPortfolio);
  const [tForm, setTForm] = useState(emptyTestimonial);

  const load = async () => {
    try {
      await api.get("/auth/me");
      const [s, l, p, t] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/leads"), api.get("/admin/portfolio"), api.get("/admin/testimonials"),
      ]);
      setStats(s.data); setLeads(l.data); setPortfolio(p.data); setTestimonials(t.data);
    } catch {
      localStorage.removeItem("mce_token");
      navigate("/admin/login");
    }
  };

  useEffect(() => { load(); }, []);

  const logout = () => { localStorage.removeItem("mce_token"); navigate("/admin/login"); };

  const savePortfolio = async (e) => {
    e.preventDefault();
    const payload = { ...pForm, services: pForm.services ? pForm.services.split(",").map((s) => s.trim()).filter(Boolean) : [] };
    if (pForm._id) {
      await api.put(`/admin/portfolio/${pForm._id}`, payload);
      toast.success("Portfolio item updated");
    } else {
      await api.post("/admin/portfolio", payload);
      toast.success("Portfolio item added");
    }
    setPForm(emptyPortfolio);
    load();
  };

  const saveTestimonial = async (e) => {
    e.preventDefault();
    await api.post("/admin/testimonials", tForm);
    toast.success("Testimonial added");
    setTForm(emptyTestimonial);
    load();
  };

  const inputCls = "lux-input";

  return (
    <main className="min-h-screen bg-[#070708] text-[#F3EEE5] pt-10 pb-20" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto px-4 sm:px-8">
        <div className="flex items-center justify-between mb-10">
          <div>
            <p className="font-display text-2xl tracking-[0.15em]">MEGA</p>
            <p className="text-[0.55rem] tracking-[0.4em] text-[#C9A55C] uppercase">Admin Studio</p>
          </div>
          <button onClick={logout} className="btn-ghost !py-2 !px-4" data-testid="admin-logout-btn"><LogOut size={13} /> Logout</button>
        </div>

        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-px bg-[#C9A55C]/12 border border-[#C9A55C]/12 mb-10" data-testid="admin-stats">
            {Object.entries(stats).map(([k, v]) => (
              <div key={k} className="bg-[#0D1018] p-5 text-center">
                <p className="font-display text-3xl text-[#C9A55C]">{v}</p>
                <p className="text-[0.6rem] uppercase tracking-[0.25em] text-[#A9ABB2] mt-1">{k.replace("_", " ")}</p>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2 mb-8" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`text-[0.65rem] uppercase tracking-[0.2em] px-4 py-2 border transition-colors ${tab === t ? "border-[#C9A55C] text-[#C9A55C] bg-[#C9A55C]/10" : "border-[#C9A55C]/20 text-[#A9ABB2]"}`}
              data-testid={`admin-tab-${t.toLowerCase()}`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "Leads" && leads && (
          <div className="space-y-10" data-testid="admin-leads-panel">
            {[
              ["Event Briefs", leads.briefs, ["full_name", "mobile", "event_type", "city", "event_date", "budget"]],
              ["Talent Enquiries", leads.talent_enquiries, ["name", "contact", "talent_category", "city", "event_date"]],
              ["Callback Requests", leads.callbacks, ["name", "phone", "message"]],
            ].map(([title, rows, cols]) => (
              <div key={title}>
                <h2 className="font-display text-xl mb-4 text-[#C9A55C]">{title} ({rows.length})</h2>
                {rows.length === 0 ? (
                  <p className="text-sm text-[#A9ABB2]">No entries yet.</p>
                ) : (
                  <div className="overflow-x-auto border border-[#C9A55C]/15">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-[#0D1018] text-left">
                          {cols.map((c) => <th key={c} className="p-3 uppercase tracking-[0.15em] text-[#A9ABB2]">{c.replace("_", " ")}</th>)}
                          <th className="p-3 uppercase tracking-[0.15em] text-[#A9ABB2]">Received</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r, i) => (
                          <tr key={i} className="border-t border-[#C9A55C]/10">
                            {cols.map((c) => <td key={c} className="p-3 text-[#F3EEE5]/85">{r[c] || "—"}</td>)}
                            <td className="p-3 text-[#A9ABB2]">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === "Portfolio" && (
          <div className="grid lg:grid-cols-2 gap-10" data-testid="admin-portfolio-panel">
            <form onSubmit={savePortfolio} className="space-y-4 border border-[#C9A55C]/20 bg-[#0D1018] p-6 h-fit">
              <h2 className="font-display text-xl text-[#C9A55C]">{pForm._id ? "Edit Item" : "Add Portfolio Item"}</h2>
              <input className={inputCls} placeholder="Title *" required value={pForm.title} onChange={(e) => setPForm({ ...pForm, title: e.target.value })} data-testid="admin-portfolio-title" />
              <select className={inputCls} value={pForm.category} onChange={(e) => setPForm({ ...pForm, category: e.target.value })} data-testid="admin-portfolio-category">
                {["Celebrity Events", "Concerts", "Weddings", "Corporate Events", "Brand Launches", "Political Events", "Private Celebrations", "Media Promotions"].map((c) => <option key={c}>{c}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-4">
                <input className={inputCls} placeholder="City" value={pForm.city} onChange={(e) => setPForm({ ...pForm, city: e.target.value })} />
                <input className={inputCls} placeholder="Date" value={pForm.date} onChange={(e) => setPForm({ ...pForm, date: e.target.value })} />
              </div>
              <input className={inputCls} placeholder="Venue" value={pForm.venue} onChange={(e) => setPForm({ ...pForm, venue: e.target.value })} />
              <input className={inputCls} placeholder="Image URL *" required value={pForm.image} onChange={(e) => setPForm({ ...pForm, image: e.target.value })} data-testid="admin-portfolio-image" />
              <textarea className={`${inputCls} min-h-[80px]`} placeholder="Description" value={pForm.description} onChange={(e) => setPForm({ ...pForm, description: e.target.value })} />
              <input className={inputCls} placeholder="Services (comma separated)" value={pForm.services} onChange={(e) => setPForm({ ...pForm, services: e.target.value })} />
              <label className="flex items-center gap-2 text-xs text-[#A9ABB2]">
                <input type="checkbox" checked={pForm.published} onChange={(e) => setPForm({ ...pForm, published: e.target.checked })} className="accent-[#C9A55C]" /> Published
              </label>
              <div className="flex gap-3">
                <button type="submit" className="btn-gold" data-testid="admin-portfolio-save-btn">{pForm._id ? "Update" : "Add Item"}</button>
                {pForm._id && <button type="button" onClick={() => setPForm(emptyPortfolio)} className="btn-ghost">Cancel</button>}
              </div>
            </form>
            <div className="space-y-3">
              {portfolio.map((p) => (
                <div key={p._id} className="flex items-center gap-4 border border-[#C9A55C]/15 bg-[#0D1018] p-4" data-testid={`admin-portfolio-row-${p._id}`}>
                  <img src={p.image} alt="" className="w-16 h-16 object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-base truncate">{p.title}</p>
                    <p className="text-[0.6rem] uppercase tracking-[0.2em] text-[#A9ABB2]">{p.category}{p.published ? "" : " · Draft"}</p>
                  </div>
                  <button onClick={() => setPForm({ ...p, services: (p.services || []).join(", ") })} className="text-xs text-[#C9A55C] underline underline-offset-4" data-testid={`admin-portfolio-edit-${p._id}`}>Edit</button>
                  <button onClick={async () => { await api.delete(`/admin/portfolio/${p._id}`); toast.success("Deleted"); load(); }} className="text-red-400/70 hover:text-red-400" data-testid={`admin-portfolio-delete-${p._id}`}><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "Testimonials" && (
          <div className="grid lg:grid-cols-2 gap-10" data-testid="admin-testimonials-panel">
            <form onSubmit={saveTestimonial} className="space-y-4 border border-[#C9A55C]/20 bg-[#0D1018] p-6 h-fit">
              <h2 className="font-display text-xl text-[#C9A55C]">Add Testimonial</h2>
              <input className={inputCls} placeholder="Client Name *" required value={tForm.name} onChange={(e) => setTForm({ ...tForm, name: e.target.value })} data-testid="admin-testimonial-name" />
              <input className={inputCls} placeholder="Role / Company" value={tForm.role} onChange={(e) => setTForm({ ...tForm, role: e.target.value })} />
              <input className={inputCls} placeholder="Event" value={tForm.event} onChange={(e) => setTForm({ ...tForm, event: e.target.value })} />
              <textarea className={`${inputCls} min-h-[90px]`} placeholder="Quote *" required value={tForm.quote} onChange={(e) => setTForm({ ...tForm, quote: e.target.value })} data-testid="admin-testimonial-quote" />
              <label className="flex items-center gap-2 text-xs text-[#A9ABB2]">
                <input type="checkbox" checked={tForm.published} onChange={(e) => setTForm({ ...tForm, published: e.target.checked })} className="accent-[#C9A55C]" /> Published
              </label>
              <button type="submit" className="btn-gold" data-testid="admin-testimonial-save-btn">Add Testimonial</button>
            </form>
            <div className="space-y-3">
              {testimonials.length === 0 && <p className="text-sm text-[#A9ABB2]">No testimonials yet. The homepage section stays hidden until one is published.</p>}
              {testimonials.map((t) => (
                <div key={t._id} className="flex items-start gap-4 border border-[#C9A55C]/15 bg-[#0D1018] p-4">
                  <div className="flex-1">
                    <p className="font-accent italic text-sm text-[#F3EEE5]/85">“{t.quote}”</p>
                    <p className="text-[0.6rem] uppercase tracking-[0.2em] text-[#C9A55C] mt-2">{t.name}{t.published ? "" : " · Draft"}</p>
                  </div>
                  <button onClick={async () => { await api.delete(`/admin/testimonials/${t._id}`); toast.success("Deleted"); load(); }} className="text-red-400/70 hover:text-red-400"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
